import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

export default function Services({ isHeadingNeeded = true }) {
  const services = [
    {
      title: "Core ESG",
      icon: "chart-line",
      subtext: "Drive ESG accountability with real-time visibility, structured reporting, and compliance control.",
      points: [
        {
          title: "Sustainability Reporting",
          content: "Align with global reporting frameworks and manage ESG disclosures on a unified platform. Track sustainability initiatives, SDG alignment, governance metrics, and materiality assessments while delivering accurate, compliant, and audit-ready ESG reports.",
        },
        {
          title: "Audit Management",
          content: "Plan and execute internal and external audits with customised checklists tailored to your industry and compliance needs. Monitor audit findings, assign responsibilities, and track corrective and preventive actions (CAPA) efficiently.",
        },
        {
          title: "Document Management",
          content: "Maintain a centralized repository for legal documents, statutory records, policies, procedures, ISO documentation, and ESG & quality records. Schedule reviews and ensure document lifecycle compliance.",
        },
      ],
    },
    {
      title: "Workforce Wellbeing",
      icon: "heart-medical",
      subtext: "Build a transparent, safe, and engaged workplace powered by digital governance tools.",
      points: [
        {
          title: "Occupational Health Center",
          content: "Centralize employee health records and medical information. Standardize health data capture, manage medical inventories, and improve workplace health program effectiveness.",
        },
        {
          title: "Grievance Management",
          content: "Enable QR-based grievance reporting, automate case assignment, investigation workflows, and resolution tracking to ensure fairness and transparency.",
        },
        {
          title: "Attrition Management",
          content: "Use predictive insights to identify early signs of employee turnover and take proactive retention measures to reduce workforce instability.",
        },
        {
          title: "Satisfaction Survey",
          content: "Design and deploy structured employee surveys to measure engagement, culture, leadership effectiveness, and workplace wellbeing.",
        },
      ],
    },
    {
      title: "Supply Chain & Performance",
      icon: "truck",
      subtext: "Create traceable, performance-driven, and compliance-ready supply chains.",
      points: [
        {
          title: "Digital Product Passport (DPP)",
          content: "Provide end-to-end product traceability with compliance-ready documentation, improving transparency for regulators, customers, and stakeholders.",
        },
        {
          title: "Supplier Sync",
          content: "Streamline supplier onboarding, collect ESG data, monitor supplier performance, and ensure continuous compliance across the supply chain network.",
        },
      ],
    },
    {
      title: "Compliance & EHS Risk",
      icon: "shield-check",
      subtext: "Proactively manage environmental impact, workplace risks, and regulatory compliance.",
      points: [
        {
          title: "Chemical Management",
          content: "Manage the complete lifecycle of chemicals — from approval and inventory tracking to SDS documentation and safe disposal. Maintain accurate chemical inventory records and ensure regulatory compliance.",
        },
        {
          title: "Environment Management",
          content: "Track and manage Scope 1, Scope 2, and Scope 3 emissions along with energy consumption, water usage, waste management, and air emissions. Monitor environmental KPIs and maintain regulatory compliance.",
        },
        {
          title: "Hazard & Risk Management",
          content: "Identify, assess, and mitigate operational risks across the organization through structured risk analysis and preventive action planning, while also enabling instant hazard reporting with our QR-based system to ensure faster response and improved workplace safety.",
        },
        {
          title: "Accident & Incident Management",
          content: "Record, track, and investigate workplace incidents efficiently. Includes root cause analysis, corrective action tracking, and compliance reporting.",
        },
      ],
    },
  ];

  return (
    <section className="relative py-16" id="features">
      <div className="container mx-auto">
        {isHeadingNeeded && (
          <div className="grid grid-cols-1 md:grid-cols-2 pb-8 items-center">
            <div>
              <h6 className="text-blue-600 text-base font-medium uppercase mb-2">
                What We Do?
              </h6>
              <h3 className="text-xl md:text-2xl font-semibold dark:text-white">
                Best Services for Your Business
              </h3>
            </div>

            <div>
              <p className="text-slate-500 dark:text-slate-300 max-w-xl">
                Modular, scalable solutions for sustainability, workforce wellbeing, supply chain transparency, and risk management.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((item, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.8, ease: "easeOut" },
                },
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -6 }}
              className="group p-6 mt-8 rounded-3xl border border-slate-200 dark:border-slate-700
                         bg-white dark:bg-slate-900
                         shadow-lg shadow-slate-100 dark:shadow-slate-800
                         transition-all duration-500 hover:bg-primary"
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center
                           bg-blue-600/10 text-blue-600
                           group-hover:bg-white group-hover:text-primary
                           transition-all duration-500"
              >
                <i className={`uil uil-${item.icon} text-3xl`} />
              </div>

              <div className="mt-6">
                <Link
                  to="#"
                  className="text-lg font-semibold text-slate-800 dark:text-white
                             group-hover:text-white transition-colors duration-300"
                >
                  {item.title}
                </Link>

                <p
                  className="text-slate-600 dark:text-slate-300 mt-3 leading-relaxed
                             group-hover:text-white/90"
                >
                  {item.subtext}
                </p>

                <ul className="mt-4 space-y-3">
                  {item.points.map((pt, idx) => (
                    <li key={idx} className="relative group/tooltip text-sm w-fit">

                      <h6
                        className="flex items-center gap-2
             font-semibold text-slate-700 dark:text-slate-200
             group-hover:text-white
             cursor-pointer"
                      >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-600 group-hover:bg-white" />
                        {pt.title}
                      </h6>

                      <div
                        className="absolute bottom-full left-0 mt-2 w-72 p-3 rounded-xl
                   bg-slate-900 dark:bg-slate-200 dark:text-black text-white text-xs
                   shadow-xl opacity-0 invisible
                   group-hover/tooltip:opacity-100
                   group-hover/tooltip:visible
                   transition-all duration-300 z-[999] border"
                      >
                        {pt.content}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
